import Icon from '@atoms/Icon/Icon';
import { ILink } from '@models/ILink';
import StickyNavPosition from '@models/enums/StickyNavPositionEnum';
import useComponentsHeightsStore from '@state/ComponentsHeightsStore';
import useStickyBuyBarStore from '@state/StickyBuyBarStore';
import useStickyNavPositionStore from '@state/StickyNavStore';
import useMediaQuery from '@utils/hooks/useMediaQuery';
import useWindowScrollPositions from '@utils/hooks/useWindowScrollPosition';
import { AnimatePresence, motion } from 'framer-motion';
import { useEffect, useState } from 'react';

interface Props {
  readonly link: ILink;
  readonly desktopBottomLimit: number;
}

const SupportButton = ({ link, desktopBottomLimit }: Props) => {
  const [bottomPosition, setBottomPosition] = useState(10);
  const { scrollY } = useWindowScrollPositions();
  const { stickyBuyBarHeight, tabbedProductNavigationBottomHeight } =
    useComponentsHeightsStore((state) => ({
      stickyBuyBarHeight: state.stickyBuyBarHeight,
      tabbedProductNavigationBottomHeight:
        state.tabbedProductNavigationBottomHeight,
    }));
  const { stickyBuyBarIsInView } = useStickyBuyBarStore((state) => ({
    stickyBuyBarIsInView: state.stickyBuyBarIsInView,
  }));
  const { stickyNavPosition } = useStickyNavPositionStore((state) => ({
    stickyNavPosition: state.stickyNavPosition,
  }));
  const isBelowLargeDesktop = useMediaQuery(1440);
  const isMobile = useMediaQuery(1024);
  const scrollDistanceForButtonVisibility = isBelowLargeDesktop ? 1000 : 2000;
  const [supportButtonIsSticky, setSupportButtonIsSticky] = useState(true);

  useEffect(() => {
    const scrollableHeight = document.documentElement.scrollHeight;
    const scrolledPosition = scrollY + window.innerHeight;
    const bottomScrollOffset = scrollableHeight - scrolledPosition;

    const supportButtonPositioningOffset = (() => {
      if (stickyBuyBarIsInView) return stickyBuyBarHeight + 20;
      if (tabbedProductNavigationBottomHeight > 0)
        return tabbedProductNavigationBottomHeight + 20;
      return isMobile ? 10 : 30;
    })();

    const supportButtonInViewCondition =
      stickyNavPosition === StickyNavPosition.Top ||
      (!isMobile && stickyNavPosition === StickyNavPosition.Bottom) ||
      stickyBuyBarIsInView;

    setSupportButtonIsSticky(supportButtonInViewCondition);

    setBottomPosition(
      bottomScrollOffset <= desktopBottomLimit && !isBelowLargeDesktop
        ? desktopBottomLimit -
            bottomScrollOffset +
            supportButtonPositioningOffset
        : supportButtonPositioningOffset
    );
  }, [
    desktopBottomLimit,
    scrollY,
    isBelowLargeDesktop,
    stickyBuyBarHeight,
    tabbedProductNavigationBottomHeight,
    isMobile,
    stickyBuyBarIsInView,
    stickyNavPosition,
  ]);

  return (
    <div id="supportButton">
      <AnimatePresence>
        {scrollY > scrollDistanceForButtonVisibility &&
          supportButtonIsSticky && (
            <motion.a
              href={link.url}
              target={link.target}
              initial={{ opacity: 0 }}
              animate={{
                opacity: 1,
                bottom: bottomPosition,
              }}
              exit={{ opacity: 0 }}
              transition={{
                opacity: { duration: 0.3 },
                bottom: { duration: 0.1 },
              }}
              className="body-four fixed left-5 z-max flex items-center gap-x-2.5 rounded-[48px] bg-grey-100 px-6 py-2.5 text-black-100 md:left-15"
            >
              <Icon name="screen_search_desktop" size="33px" />

              {link.name}
            </motion.a>
          )}
      </AnimatePresence>
    </div>
  );
};

export default SupportButton;
